
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { toast } from 'sonner';

export const useDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savedResponses, setSavedResponses] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { user } = useAuth();

  const fetchSavedResponses = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase
        .from('saved_responses')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      setSavedResponses(data || []);
    } catch (error: any) {
      console.error('Error fetching saved responses:', error);
      setError(error.message || 'Failed to load your saved responses');
      toast.error('Failed to load your saved responses');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSavedResponses();
  }, [fetchSavedResponses]);

  const refetch = useCallback(() => {
    return fetchSavedResponses();
  }, [fetchSavedResponses]);

  const handleDelete = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from('saved_responses')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      setSavedResponses(prev => prev.filter(item => item.id !== id));
      toast.success('Response deleted successfully');
    } catch (error: any) {
      console.error('Error deleting response:', error);
      toast.error('Failed to delete response');
    }
  }, []);

  const handleMultipleDelete = useCallback(async () => {
    try {
      // Delete all selected items
      const { error } = await supabase
        .from('saved_responses')
        .delete()
        .in('id', selectedItems);
      
      if (error) throw error;
      
      setSavedResponses(prev => prev.filter(item => !selectedItems.includes(item.id)));
      setSelectedItems([]);
      setIsDeleteModalOpen(false);
      toast.success('Responses deleted successfully');
    } catch (error: any) {
      console.error('Error deleting multiple responses:', error);
      toast.error('Failed to delete responses');
    }
  }, [selectedItems]);

  const toggleItemSelection = useCallback((id: string) => {
    setSelectedItems(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id) 
        : [...prev, id]
    );
  }, []);

  const selectAllItems = useCallback((isSelected: boolean) => {
    if (isSelected) {
      // Get IDs from filteredResponses
      const idsToSelect = filteredResponses.map(item => item.id);
      setSelectedItems(idsToSelect);
    } else {
      setSelectedItems([]);
    }
  }, [savedResponses, activeTab]);

  // Filter responses based on active tab
  const filteredResponses = savedResponses.filter(response => {
    if (activeTab === 'all') return true;
    return response.tool_type === activeTab;
  });

  return {
    loading,
    error,
    savedResponses,
    filteredResponses,
    activeTab,
    setActiveTab,
    selectedItems,
    setSelectedItems,
    isDeleteModalOpen,
    setIsDeleteModalOpen,
    handleDelete,
    handleMultipleDelete,
    toggleItemSelection,
    selectAllItems,
    refetch
  };
};
